/**
 * Profile + preferences hook, backed by `GET /auth/me` and `PATCH /auth/me/preferences`.
 *
 * Deliberately separate from the existing `useAuthStore`: that store holds
 * the tokens + minimum identity for routing, and adding mutation machinery
 * to Zustand there would be awkward. This hook owns UI-preferences state
 * (onboarding dismissals, future tour flags) via React Query so caching +
 * invalidation stay consistent with the rest of the app.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const unwrap = <T>(payload: ApiEnvelope<T> | T): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in (payload as object) &&
    'data' in (payload as object)
  ) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
};

export interface MyProfile {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: {
    id: string | null;
    name: string | null;
    permissions: Array<{
      id: string;
      name: string;
      resource: string;
      action: string;
    }>;
  };
  isActive: boolean;
  lastLogin: string | null;
  twoFactorEnabled: boolean;
  /**
   * True after an owner issued a one-time temporary password — the app
   * shell blocks behind a mandatory change-password gate until the user
   * rotates it. Cleared by any successful password change/reset.
   */
  mustChangePassword?: boolean;
  /** Doctor's primary specialty (canonical value) or null if not picked yet. */
  specialty: string | null;
  /** Free-text refinement; null if not provided. */
  subspecialty: string | null;
  /**
   * Per-user UI preferences. Known keys:
   *  - voiceprintPromptDismissedAt: ISO timestamp, set when the doctor
   *    dismisses the first-login enrollment modal.
   *  - voiceprintEnrolledAt: ISO timestamp, mirrors the latest enrollment
   *    from `user_voiceprints` for quick frontend gating.
   *  - specialtyPromptDismissedAt: ISO timestamp, set when the doctor
   *    dismisses the first-login specialty picker without picking one.
   *  - demoConsultCompletedAt / demoConsultSkippedAt: ISO timestamps —
   *    the /welcome first-run demo consult finished (review page
   *    reached) or was skipped. Gate /welcome's self-redirect.
   *  - setupChecklistDismissedAt: ISO timestamp — home Getting-started
   *    checklist dismissed (server-side so it follows the user).
   *  - roleWelcomeDismissedAt: ISO timestamp — role-home welcome card
   *    dismissed (one key shared by all role variants).
   */
  preferences: Record<string, unknown>;
  /**
   * For Nurse/Receptionist/Billing members: the user id of the doctor
   * they assist for this active tenant. Null if unassigned, or for roles
   * that aren't assistable (Owner, Doctor). Drives queue scoping on the
   * non-doctor home pages.
   */
  linkedDoctorId: string | null;
  /**
   * Display info for the linked doctor, resolved server-side so role
   * homes don't need to call the OWNER-gated /tenants/:id/members
   * endpoint just to render a name.
   */
  linkedDoctor: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export const profileKeys = {
  me: ['profile', 'me'] as const,
};

export function useMyProfile(enabled: boolean = true) {
  return useQuery<MyProfile>({
    queryKey: profileKeys.me,
    queryFn: async () => {
      const res = await apiClient.get<ApiEnvelope<MyProfile> | MyProfile>(
        '/auth/me',
      );
      return unwrap(res.data);
    },
    staleTime: 60_000,
    enabled,
  });
}

export interface UpdateProfilePatch {
  specialty?: string | null;
  subspecialty?: string | null;
  firstName?: string;
  lastName?: string;
}

interface ProfileUpdateResponse {
  firstName?: string;
  lastName?: string;
  specialty: string | null;
  subspecialty: string | null;
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: UpdateProfilePatch) => {
      const res = await apiClient.patch<
        ApiEnvelope<ProfileUpdateResponse> | ProfileUpdateResponse
      >('/auth/me/profile', patch);
      // Stamp "the doctor has explicitly chosen a specialty" whenever a
      // specialty write succeeds — centrally, so every entry point
      // (welcome picker, settings, first-login modal) is covered. The
      // value alone can't carry this: `general` is BOTH the unset
      // default and a legitimate choice (a GP), so checking `!==
      // 'general'` told every GP their setup was unfinished forever.
      if (typeof patch.specialty === 'string') {
        void apiClient
          .patch('/auth/me/preferences', {
            specialtyPickedAt: new Date().toISOString(),
          })
          .then(() => {
            void qc.invalidateQueries({ queryKey: profileKeys.me });
          })
          .catch(() => {
            /* non-blocking — the checklist item just stays unticked */
          });
      }
      return unwrap(res.data);
    },
    onSuccess: (next) => {
      qc.setQueryData<MyProfile | undefined>(profileKeys.me, (curr) => {
        if (!curr) return curr;
        return {
          ...curr,
          specialty: next.specialty,
          subspecialty: next.subspecialty,
          firstName: next.firstName ?? curr.firstName,
          lastName: next.lastName ?? curr.lastName,
        };
      });
      // Sidebar + header read the user's name from useAuthStore (it
      // owns the auth-bound identity), not from the profile query.
      // Without this push, the doctor saves a new name in Settings
      // and the sidebar keeps showing the old one until logout.
      if (
        typeof next.firstName === 'string' ||
        typeof next.lastName === 'string'
      ) {
        const authState = useAuthStore.getState();
        if (authState.user) {
          const firstName = next.firstName ?? authState.user.firstName;
          const lastName = next.lastName ?? authState.user.lastName;
          useAuthStore.setState({
            user: {
              ...authState.user,
              firstName,
              lastName,
              displayName: `${firstName} ${lastName}`.trim(),
            },
          });
        }
      }
    },
  });
}

export function useChangeMyPassword() {
  return useMutation({
    mutationFn: async (input: {
      currentPassword: string;
      newPassword: string;
    }) => {
      // 204 No Content — body is empty; we don't touch the cache (the
      // user row didn't change shape, only password+passwordChangedAt).
      await apiClient.post('/auth/me/change-password', input);
    },
  });
}

export function useUpdateMyPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const res = await apiClient.patch<
        | ApiEnvelope<{ preferences: Record<string, unknown> }>
        | { preferences: Record<string, unknown> }
      >('/auth/me/preferences', patch);
      return unwrap(res.data).preferences;
    },
    onSuccess: (nextPrefs) => {
      // Optimistically merge into cache so the UI flips immediately without
      // a refetch. The backend response is authoritative; this just prevents
      // a flash between click and refetch.
      qc.setQueryData<MyProfile | undefined>(profileKeys.me, (curr) =>
        curr ? { ...curr, preferences: nextPrefs } : curr,
      );
    },
  });
}
